/* eslint-disable sonarjs/cognitive-complexity */
import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import Button from '@tcp/core/src/components/common/atoms/Button';
import { getLocalStorage } from '@tcp/core/src/utils/localStorageManagement';
import {
  getSessionStorage,
  getShopThisStoreParams,
  getUrlParameter,
  removeSessionStorage,
  routerPush,
  routerReplace,
  setSessionStorage,
} from '@tcp/core/src/utils';
import {
  SHOP_THIS_STORE_ACCESSIBLE_COMPONENTS,
  SHOP_THIS_STORE_URL_QUERY_KEY,
  SAVED_SHOP_THIS_STORE_URL,
  SHOP_THIS_STORE_SCROLL,
} from '@tcp/core/src/constants/shopThisStore.constant';
import {
  SearchStoreCategoryWrapper,
  ShopThisStoreCategoryGrid,
  ShopThisStoreCategoryRow,
} from '../styles/CategoriesArea.style';

import { L2CategoriesSkeleton } from './CategoriesFilterSkeleton.view';

const useIntersectionObserver = (options = {}, defaultVisibility = false) => {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(defaultVisibility);

  const callbackFn = (entries) => {
    const [entry] = entries;
    setIsVisible(entry.isIntersecting);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(callbackFn, options);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
    };
  }, [containerRef, options]);

  return [containerRef, isVisible];
};

export const L2Categories = (props) => {
  const { navigationL2Data, handleL2categorySelection, shopThisStoreL2CategoryGroupingSelection } =
    props;
  return (
    <ShopThisStoreCategoryGrid>
      <ShopThisStoreCategoryRow className="categories-l2-row-desktop">
        <div className="categories-l2-button-wrapper">
          {navigationL2Data &&
            navigationL2Data.map((item) => {
              return (
                <>
                  {item && item.label && (
                    <Button
                      onClick={() => {
                        handleL2categorySelection(item);
                        setSessionStorage({ key: SHOP_THIS_STORE_SCROLL, value: window.scrollY });
                      }}
                      id="l2-categories-btn"
                      buttonVariation="variable-width"
                      className={`categories-button ${
                        shopThisStoreL2CategoryGroupingSelection &&
                        shopThisStoreL2CategoryGroupingSelection.label === item.label
                          ? 'button-active'
                          : ''
                      }`}
                    >
                      {item.label}
                    </Button>
                  )}
                </>
              );
            })}
        </div>
      </ShopThisStoreCategoryRow>
    </ShopThisStoreCategoryGrid>
  );
};

L2Categories.propTypes = {
  navigationL2Data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  shopThisStoreL2CategoryGroupingSelection: PropTypes.shape({}).isRequired,
  handleL2categorySelection: PropTypes.func.isRequired,
};

export const L1Categories = (props) => {
  const { selectedStoreL1Categories, shopThisStoreL1categorySelection, handleL1categorySelection } =
    props;
  const categoriesWrapper = useRef(null);
  const buttonRef = useRef(null);
  const [isLeftArrowEnabled, setIsLeftArrowEnabled] = useState(false);
  const [isRightArrowEnabled, setIsRightArrowEnabled] = useState(false);

  const [firstElementRef, isStartTileInView] = useIntersectionObserver({
    root: null,
    rootMargin: '0px',
  });
  const [lastElementRef, isEndTileInView] = useIntersectionObserver({
    root: null,
    rootMargin: '0px',
  });

  const doScroll = (scrollOffset) => {
    categoriesWrapper.current.scrollLeft += scrollOffset;
    return categoriesWrapper.current.scrollLeft;
  };

  const handleScroll = (scrollOffset) => {
    doScroll(scrollOffset);
  };

  const getElementRef = (index) => {
    if (index === 0) return firstElementRef;
    if (index === selectedStoreL1Categories.length - 1) return lastElementRef;
    return buttonRef;
  };

  useEffect(() => {
    if (isStartTileInView && !isEndTileInView) {
      setIsLeftArrowEnabled(false);
      setIsRightArrowEnabled(true);
    } else if (isEndTileInView && !isStartTileInView) {
      setIsRightArrowEnabled(false);
      setIsLeftArrowEnabled(true);
    } else if (isStartTileInView && isEndTileInView) {
      setIsLeftArrowEnabled(false);
      setIsRightArrowEnabled(false);
    }
  }, [isStartTileInView, isEndTileInView]);

  return (
    <ShopThisStoreCategoryGrid>
      <ShopThisStoreCategoryRow className="categories-l1-row-desktop">
        <Button
          id="scroll-left-btn"
          onClick={() => handleScroll(-1000)}
          disabled={!isLeftArrowEnabled}
          buttonVariation="variable-width"
          className={`arrow-button ${!isLeftArrowEnabled ? 'button-disabled' : ''}`}
        >
          <i className="arrow left" />
        </Button>
        <div className="categories-l1-button-wrapper" ref={categoriesWrapper}>
          {selectedStoreL1Categories &&
            selectedStoreL1Categories.map((item, index) => {
              return (
                <Button
                  ref={getElementRef(index)}
                  key={item.categoryId}
                  onClick={() => {
                    handleL1categorySelection(item, true);
                    setSessionStorage({ key: SHOP_THIS_STORE_SCROLL, value: window.scrollY });
                  }}
                  id="l1-categories-btn"
                  buttonVariation="variable-width"
                  className={`categories-button ${
                    shopThisStoreL1categorySelection &&
                    shopThisStoreL1categorySelection.categoryId === item.categoryId
                      ? 'button-active'
                      : ''
                  }`}
                >
                  {item.categoryContent.name}
                </Button>
              );
            })}
        </div>
        <Button
          id="scroll-right-btn"
          onClick={() => handleScroll(1000)}
          disabled={!isRightArrowEnabled}
          buttonVariation="variable-width"
          className={`arrow-button ${!isRightArrowEnabled ? 'button-disabled' : ''}`}
        >
          <i className="arrow right" />
        </Button>
      </ShopThisStoreCategoryRow>
    </ShopThisStoreCategoryGrid>
  );
};

L1Categories.propTypes = {
  selectedStoreL1Categories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  shopThisStoreL1categorySelection: PropTypes.shape({}).isRequired,
  handleL1categorySelection: PropTypes.func.isRequired,
};

export const StoreCategoryNavigation = (props) => {
  const {
    router,
    defaultStore,
    selectedStoreL1Categories,
    selectedStoreL2Categories,
    shopThisStoreL1categorySelection,
    shopThisStoreL2CategoryGroupingSelection,
    setL1CategorySelection,
    setL1CategorySelectionLoaded,
    setL2CategorySelection,
    setL2CategorySelectionLoaded,
    getL2Categories,
    setL1CategorySelectionCategoryContent,
    setL2CategorySelectionCategoryGroupId,
    trackAnalyticsClick,
    isShopThisStoreL2categoriesLoading,
    isShopThisStoreL1categoriesLoading,
  } = props;
  let params = !isEmpty(defaultStore) ? getShopThisStoreParams(defaultStore?.basicInfo) : '';

  const handleL1categorySelection = (item) => {
    if (!isEmpty(item)) {
      setL1CategorySelectionLoaded(false);
      setL2CategorySelectionLoaded(false);
      setL1CategorySelection(item);
      setL1CategorySelectionCategoryContent(item.categoryContent);
      const storeId = defaultStore.basicInfo.id || router.query.storeNameId.split('-')[4];
      getL2Categories(storeId, item.categoryContent.name, router?.query?.categoryGrouping);
      setL1CategorySelectionLoaded(true);
    }
  };

  const getAnalyticFormattedString = (item) => {
    return `shopthestore-category-${shopThisStoreL1categorySelection?.categoryContent?.name}`.concat(
      !isEmpty(item) ? `-${item?.label}` : ''
    );
  };

  const trackAnalyticsCategory = (item) => {
    trackAnalyticsClick(
      {
        nav_cd171: getAnalyticFormattedString(item),
        customEvents: ['event82'],
      },
      {
        name: getAnalyticFormattedString(item),
      }
    );
  };

  const handleL2categorySelection = (item) => {
    if (!isEmpty(item)) {
      setL2CategorySelection(item);
      setL2CategorySelectionCategoryGroupId(item.items[0].categoryContent.groupIdentifier);
      setL2CategorySelectionLoaded(true);
    }
  };

  const handleDefaultL1Selection = () => {
    if (router.query && router.query.category) {
      if (
        router?.query?.storeNameId === getSessionStorage(SAVED_SHOP_THIS_STORE_URL)?.split('/')[2]
      ) {
        const category = selectedStoreL1Categories.find(
          (el) => el.categoryContent.name === router.query.category
        );
        handleL1categorySelection(category);
      }
    } else {
      handleL1categorySelection(selectedStoreL1Categories[0]);
    }
  };

  const handleRouteChange = (storeId, category, categoryGroupLabel) => {
    setSessionStorage({
      key: SAVED_SHOP_THIS_STORE_URL,
      value: `/shop-this-store/${storeId}/${category}/${categoryGroupLabel}`,
    });
    if (getUrlParameter('from') === 'breadcrumbs') removeSessionStorage(SHOP_THIS_STORE_SCROLL);
    if (
      selectedStoreL1Categories.length &&
      router?.pathname === SHOP_THIS_STORE_ACCESSIBLE_COMPONENTS.SHOP_THIS_STORE
    ) {
      routerReplace(
        `/shop-this-store?storeNameId=${storeId}&category=${category}&categoryGrouping=${categoryGroupLabel}&mode=${SHOP_THIS_STORE_URL_QUERY_KEY}`,
        `/shop-this-store/${storeId}/${category}/${categoryGroupLabel}?mode=${SHOP_THIS_STORE_URL_QUERY_KEY}`
      );
    } else if (router?.pathname === SHOP_THIS_STORE_ACCESSIBLE_COMPONENTS.SEARCH) {
      routerPush(
        `/shop-this-store?storeNameId=${storeId}&category=${category}&categoryGrouping=${categoryGroupLabel}&mode=${SHOP_THIS_STORE_URL_QUERY_KEY}`,
        `/shop-this-store/${storeId}/${category}/${categoryGroupLabel}?mode=${SHOP_THIS_STORE_URL_QUERY_KEY}`
      );
    } else if (router?.pathname === SHOP_THIS_STORE_ACCESSIBLE_COMPONENTS.SHOP_THIS_STORE) {
      routerReplace(
        `/shop-this-store?storeNameId=${storeId}&mode=${SHOP_THIS_STORE_URL_QUERY_KEY}`,
        `/shop-this-store/${storeId}?mode=${SHOP_THIS_STORE_URL_QUERY_KEY}`
      );
    }
  };

  useEffect(() => {
    if (
      !isEmpty(shopThisStoreL1categorySelection) &&
      !isEmpty(shopThisStoreL2CategoryGroupingSelection)
    ) {
      handleRouteChange(
        params,
        shopThisStoreL1categorySelection.categoryContent.name,
        shopThisStoreL2CategoryGroupingSelection.label
      );
    }
  }, [shopThisStoreL1categorySelection, shopThisStoreL2CategoryGroupingSelection]);

  useEffect(() => {
    if (!isEmpty(shopThisStoreL2CategoryGroupingSelection)) {
      document.getElementById('drawer-wrapper').style.pointerEvents = 'none';
      trackAnalyticsCategory(shopThisStoreL2CategoryGroupingSelection);
    }
  }, [shopThisStoreL2CategoryGroupingSelection]);

  useEffect(() => {
    if (
      router?.pathname === SHOP_THIS_STORE_ACCESSIBLE_COMPONENTS.SHOP_THIS_STORE &&
      !isShopThisStoreL1categoriesLoading &&
      selectedStoreL1Categories?.length > 0
    ) {
      handleDefaultL1Selection();
    } else {
      setL1CategorySelection({});
      setL1CategorySelectionCategoryContent({});
    }
  }, [isShopThisStoreL1categoriesLoading]);

  useEffect(() => {
    if (
      router?.pathname === SHOP_THIS_STORE_ACCESSIBLE_COMPONENTS.SHOP_THIS_STORE &&
      router.query &&
      !router.query.category &&
      !router.query.categoryGrouping
    ) {
      params = !isEmpty(defaultStore) ? getShopThisStoreParams(defaultStore?.basicInfo) : '';
      routerReplace(
        `/shop-this-store?storeNameId=${params}&mode=${SHOP_THIS_STORE_URL_QUERY_KEY}`,
        `/shop-this-store/${params}?mode=${SHOP_THIS_STORE_URL_QUERY_KEY}`
      );
    }
  }, [getLocalStorage('defaultStore')]);

  return (
    <>
      {!isEmpty(selectedStoreL1Categories) && (
        <L1Categories
          selectedStoreL1Categories={selectedStoreL1Categories}
          shopThisStoreL1categorySelection={shopThisStoreL1categorySelection}
          handleL1categorySelection={handleL1categorySelection}
        />
      )}
      {router?.pathname === SHOP_THIS_STORE_ACCESSIBLE_COMPONENTS.SHOP_THIS_STORE && (
        <>
          {isShopThisStoreL2categoriesLoading && <L2CategoriesSkeleton />}
          {!isShopThisStoreL2categoriesLoading &&
            !isEmpty(shopThisStoreL1categorySelection) &&
            !isEmpty(selectedStoreL2Categories) && (
              <L2Categories
                navigationL2Data={selectedStoreL2Categories}
                shopThisStoreL2CategoryGroupingSelection={shopThisStoreL2CategoryGroupingSelection}
                handleL2categorySelection={handleL2categorySelection}
              />
            )}
        </>
      )}
    </>
  );
};

StoreCategoryNavigation.propTypes = {
  router: PropTypes.shape({}).isRequired,
  defaultStore: PropTypes.shape({}).isRequired,
  selectedStoreL1Categories: PropTypes.array.isRequired,
  selectedStoreL2Categories: PropTypes.array.isRequired,
  shopThisStoreL1categorySelection: PropTypes.shape({}).isRequired,
  shopThisStoreL2CategoryGroupingSelection: PropTypes.shape({}).isRequired,
  setL1CategorySelection: PropTypes.func.isRequired,
  setL2CategorySelection: PropTypes.func.isRequired,
  getL2Categories: PropTypes.func.isRequired,
  setL1CategorySelectionCategoryContent: PropTypes.func.isRequired,
  setL2CategorySelectionCategoryGroupId: PropTypes.func.isRequired,
  isShopThisStoreL1categoriesLoading: PropTypes.bool.isRequired,
  trackAnalyticsClick: PropTypes.func,
  isShopThisStoreL2categoriesLoading: PropTypes.bool.isRequired,
  setL1CategorySelectionLoaded: PropTypes.func.isRequired,
  setL2CategorySelectionLoaded: PropTypes.func.isRequired,
};

const CategoriesArea = (props) => {
  const {
    router,
    defaultStore,
    shopThisStoreL1categorySelection,
    shopThisStoreL2CategoryGroupingSelection,
    setL1CategorySelection,
    setL2CategorySelection,
    getL2Categories,
    setL1CategorySelectionCategoryContent,
    setL2CategorySelectionCategoryGroupId,
    trackAnalyticsClick,
    isShopThisStoreL1categoriesLoading,
    isShopThisStoreL2categoriesLoading,
    setL1CategorySelectionLoaded,
    setL2CategorySelectionLoaded,
    selectedStoreL1Categories,
    selectedStoreL2Categories,
  } = props;

  return (
    <>
      {(router?.pathname === SHOP_THIS_STORE_ACCESSIBLE_COMPONENTS.SHOP_THIS_STORE ||
        router?.pathname === SHOP_THIS_STORE_ACCESSIBLE_COMPONENTS.SEARCH) && (
        <SearchStoreCategoryWrapper>
          <StoreCategoryNavigation
            router={router}
            defaultStore={defaultStore}
            selectedStoreL1Categories={selectedStoreL1Categories}
            selectedStoreL2Categories={selectedStoreL2Categories}
            shopThisStoreL1categorySelection={shopThisStoreL1categorySelection}
            shopThisStoreL2CategoryGroupingSelection={shopThisStoreL2CategoryGroupingSelection}
            setL1CategorySelection={setL1CategorySelection}
            setL2CategorySelection={setL2CategorySelection}
            setL1CategorySelectionLoaded={setL1CategorySelectionLoaded}
            setL2CategorySelectionLoaded={setL2CategorySelectionLoaded}
            getL2Categories={getL2Categories}
            setL1CategorySelectionCategoryContent={setL1CategorySelectionCategoryContent}
            setL2CategorySelectionCategoryGroupId={setL2CategorySelectionCategoryGroupId}
            trackAnalyticsClick={trackAnalyticsClick}
            isShopThisStoreL1categoriesLoading={isShopThisStoreL1categoriesLoading}
            isShopThisStoreL2categoriesLoading={isShopThisStoreL2categoriesLoading}
          />
        </SearchStoreCategoryWrapper>
      )}
    </>
  );
};

CategoriesArea.propTypes = {
  navigationData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  router: PropTypes.shape({}).isRequired,
  defaultStore: PropTypes.shape({}).isRequired,
  shopThisStoreL1categories: PropTypes.shape({}).isRequired,
  shopThisStoreL2categories: PropTypes.shape({}).isRequired,
  shopThisStoreL1categorySelection: PropTypes.shape({}).isRequired,
  shopThisStoreL2CategoryGroupingSelection: PropTypes.shape({}).isRequired,
  setL1CategorySelection: PropTypes.func.isRequired,
  setL2CategorySelection: PropTypes.func.isRequired,
  getL2Categories: PropTypes.func.isRequired,
  setL1CategorySelectionCategoryContent: PropTypes.func.isRequired,
  setL2CategorySelectionCategoryGroupId: PropTypes.func.isRequired,
  trackAnalyticsClick: PropTypes.func,
  isShopThisStoreL1categoriesLoading: PropTypes.bool.isRequired,
  isShopThisStoreL2categoriesLoading: PropTypes.bool.isRequired,
  setL1CategorySelectionLoaded: PropTypes.func.isRequired,
  setL2CategorySelectionLoaded: PropTypes.func.isRequired,
};

export default CategoriesArea;
